import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import CookieBanner from "@/components/CookieBanner";
import SEO from "@/components/SEO";

// ERP app
import Login from "@/components/app/Login";
import Register from "@/components/app/Register";
import AuthCallback from "@/components/app/AuthCallback";
import AppLayout from "@/components/app/AppLayout";
import Dashboard from "@/components/app/Dashboard";
import Customers from "@/components/app/Customers";
import Items from "@/components/app/Items";
import Warehouses from "@/components/app/Warehouses";
import Sales from "@/components/app/Sales";
import Procurement from "@/components/app/Procurement";
import HR from "@/components/app/HR";
import Finance from "@/components/app/Finance";
import Operations from "@/components/app/Operations";
import IT from "@/components/app/IT";
import Financials from "@/components/app/Financials";
import Billing from "@/components/app/Billing";
import Landing from "@/components/landing/Landing";
import Jobs from "@/components/app/Jobs";
import Ledger from "@/components/app/Ledger";
import TenantsAdmin from "@/components/app/TenantsAdmin";
import QuotesAdmin from "@/components/app/QuotesAdmin";
import Notifications from "@/components/app/Notifications";
import CRM from "@/components/app/CRM";
import Reports from "@/components/app/Reports";
import Upgrade from "@/components/app/Upgrade";
import TenantThemeProvider from "@/contexts/TenantThemeProvider";
import Privacy from "@/components/legal/Privacy";
import Terms from "@/components/legal/Terms";

// Roadmap (existing marketing page)
import Nav from "@/components/erpgo/Nav";
import Hero from "@/components/erpgo/Hero";
import ExecSummary from "@/components/erpgo/ExecSummary";
import Phases from "@/components/erpgo/Phases";
import Modules from "@/components/erpgo/Modules";
import Gr8Agentic from "@/components/erpgo/Gr8Agentic";
import WhiteLabel from "@/components/erpgo/WhiteLabel";
import Kpis from "@/components/erpgo/Kpis";
import Integrations from "@/components/erpgo/Integrations";
import Roles from "@/components/erpgo/Roles";
import Risks from "@/components/erpgo/Risks";
import CTA from "@/components/erpgo/CTA";

function Roadmap() {
  return (
    <div className="bg-white text-[#0A0A0A] antialiased" data-testid="roadmap-page">
      <SEO
        title="Gr8Boost ERP — Product Roadmap"
        description="The public Gr8Boost ERP product roadmap: modules, phases, integrations, AI co-pilot scope and white-label capabilities."
        canonical="https://gr8booster.com/roadmap"
      />
      <Nav />
      <main className="pt-16">
        <Hero />
        <ExecSummary />
        <Phases />
        <Modules />
        <Gr8Agentic />
        <WhiteLabel />
        <Kpis />
        <Integrations />
        <Roles />
        <Risks />
        <CTA />
      </main>
    </div>
  );
}

// Handles `#session_id=...` from Emergent Google Auth at any URL
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/roadmap" element={<Roadmap />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route element={<AppLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/items" element={<Items />} />
        <Route path="/warehouses" element={<Warehouses />} />
        <Route path="/sales" element={<Sales />} />
        <Route path="/procurement" element={<Procurement />} />
        <Route path="/hr" element={<HR />} />
        <Route path="/finance" element={<Finance />} />
        <Route path="/financials" element={<Financials />} />
        <Route path="/operations" element={<Operations />} />
        <Route path="/it" element={<IT />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/billing/upgrade" element={<Upgrade />} />
        <Route path="/jobs" element={<Jobs />} />
        <Route path="/ledger" element={<Ledger />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/crm" element={<CRM />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/admin/tenants" element={<TenantsAdmin />} />
        <Route path="/admin/quotes" element={<QuotesAdmin />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TenantThemeProvider>
            <AppRouter />
            <CookieBanner />
            <Toaster />
          </TenantThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
